import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { useNavigate } from 'react-router-dom';
import { defaultTutorSearchPath } from '../../utils/tutorSearchMode';
import { userApiClient } from '../../userApiClient';
import { groupUserTutorBookings } from '../../utils/userTutorBookingGrouping';
import {
  TutorBookingFilter,
  UserTutorBookingListItem,
  UserTutorBookingSummaryCountsResponse,
} from '../../models/Tutor';
import TutorBookingChangeProposalScheduleChip from '../../components/tutor/TutorBookingChangeProposalScheduleChip';
import TutorBookingScheduleBlock from '../../components/tutor/TutorBookingScheduleBlock';
import { TutorBookingChangeStatus } from '../../models/TutorBookingChange';
import {
  formatTutorBookingScheduleLabels,
  getProposalNewEndIso,
  getProposalOldEndIso,
  getTutorBookingPreviousTimeLabel,
  proposalTimeChanged,
} from '../../utils/tutorBookingChangeProposal';
import UserTutorSeriesBookingCard from '../../components/user/UserTutorSeriesBookingCard';
import SEO from '../../components/SEO';
import BookingCancellationPolicy from '../../components/BookingCancellationPolicy';
import BookingCreatedAtLabel from '../../components/BookingCreatedAtLabel';
import TutorLessonSessionTypeChip from '../../components/tutor/TutorLessonSessionTypeChip';
import TutorBookingPaymentStatusChips from '../../components/tutor/TutorBookingPaymentStatusChips';
import TutorLessonAttendanceChip from '../../components/tutor/TutorLessonAttendanceChip';
import TutorBookingFilterTabs from '../../components/tutor/TutorBookingFilterTabs';
import {
  extractTutorBookingsPageTotal,
  USER_TUTOR_BOOKING_FILTERS,
} from '../../utils/tutorBookingFilters';
import { notifyTutorBookingNeedsActionCountChanged } from '../../utils/tutorBookingNeedsActionCount';
import {
  canUserConfirmTutorLessonAttendance,
  getTutorBookingStatusChipColor,
  getTutorBookingStatusChipVariant,
  isPastTutorBookingStart,
  isUserTutorBookingCancelledStatus,
  normalizeTutorBookingStatus,
  shouldShowTutorLessonAttendanceUi,
} from '../../utils/tutorBookingStatus';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import { parseBookingDisplayInstant } from '../../utils/bookingInstant';
import { shouldShowCancellationPolicyForPayment } from '../../utils/formatCancellationPolicy';
import { getTutorLessonConfirmationErrorMessage } from '../../utils/tutorLessonConfirmation';
import PassBookingChip from '../../components/passes/PassBookingChip';
import TutorSchoolAffiliation from '../../components/tutor/TutorSchoolAffiliation';
import ChatWithUserIconButton from '../../components/chat/ChatWithUserIconButton';

const USER_TUTOR_BOOKINGS_MAX_WIDTH = 1200;

function parseBookingStart(iso: string): Date {
  return parseBookingDisplayInstant(iso);
}

function parseBookingEnd(iso: string): Date {
  return parseBookingDisplayInstant(iso);
}

function shortRef(id: string): string {
  if (!id || id.length < 10) return id || '—';
  return `${id.slice(0, 8)}…`;
}

export default function UserTutorBookingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<TutorBookingFilter>('needs_user_action');
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bookings, setBookings] = useState<UserTutorBookingListItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [summary, setSummary] = useState<UserTutorBookingSummaryCountsResponse | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedForCancel, setSelectedForCancel] = useState<UserTutorBookingListItem | null>(null);
  const [cancelSeriesId, setCancelSeriesId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmingBookingId, setConfirmingBookingId] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await userApiClient.getTutorBookingsSummary();
      setSummary(data);
    } catch {
      // Summary failure should not block the list; counts stay at 0 until retry.
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userApiClient.getTutorBookingsPage(activeFilter, page, rowsPerPage);
      setBookings(response.content ?? []);
      setTotalElements(extractTutorBookingsPageTotal(response));
      setError(null);
      setHasLoadedOnce(true);
    } catch {
      setError(t('userTutorBookings.fetchError', 'Failed to load tutor bookings'));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, page, rowsPerPage, t]);

  const refreshAfterMutation = useCallback(async () => {
    await Promise.all([fetchBookings(), fetchSummary()]);
    notifyTutorBookingNeedsActionCountChanged();
  }, [fetchBookings, fetchSummary]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    setPage(0);
  }, [activeFilter]);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) {
      return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    }
    if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    }
    return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
  };

  const formatBookingSchedule = (
    startTime: string,
    endTime: string,
    options?: { timezone?: string | null; source?: 'display' | 'proposal' },
  ) => formatTutorBookingScheduleLabels(startTime, endTime, i18n.language, formatDuration, options);

  const formatMoney = (amount: number, currencyCode: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode || 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const statusLabel = (status: string) => {
    const key = `userTutorBookings.bookingStatus.${normalizeTutorBookingStatus(status)}`;
    return t(key, status);
  };

  const renderChangeProposalBadge = (
    booking: UserTutorBookingListItem,
    opts?: { showPreviousTimeInTooltip?: boolean },
  ) => {
    if (!booking.changeProposal) return null;
    return (
      <TutorBookingChangeProposalScheduleChip
        proposal={booking.changeProposal}
        bookingStartTime={booking.startTime}
        bookingEndTime={booking.endTime}
        locale={i18n.language}
        audience="user"
        timezone={booking.timezone}
        showPreviousTimeInTooltip={opts?.showPreviousTimeInTooltip ?? true}
      />
    );
  };

  const hasPendingChangeProposal = (booking: UserTutorBookingListItem) =>
    booking.changeProposal?.status === TutorBookingChangeStatus.PENDING_REPLY;

  const renderCardScheduleSection = (booking: UserTutorBookingListItem) => {
    const proposal = booking.changeProposal;
    const previousVisibleInline =
      hasPendingChangeProposal(booking) && !!proposal && proposalTimeChanged(proposal);
    const statusChip = renderChangeProposalBadge(booking, {
      showPreviousTimeInTooltip: !previousVisibleInline,
    });

    if (hasPendingChangeProposal(booking) && proposal) {
      const proposalScheduleOptions = { source: 'proposal' as const, timezone: booking.timezone };
      const proposedEndIso = getProposalNewEndIso(proposal, booking.startTime, booking.endTime);
      const proposedSchedule = formatBookingSchedule(
        proposal.newStartTime,
        proposedEndIso,
        proposalScheduleOptions,
      );

      if (proposalTimeChanged(proposal)) {
        const oldEndIso = getProposalOldEndIso(proposal, booking.startTime, booking.endTime);
        const previousSchedule = formatBookingSchedule(
          proposal.oldStartTime,
          oldEndIso,
          proposalScheduleOptions,
        );
        return (
          <TutorBookingScheduleBlock
            schedule={proposedSchedule}
            emphasize
            statusChip={statusChip}
            previousTimeLabel={getTutorBookingPreviousTimeLabel(previousSchedule, proposedSchedule)}
          />
        );
      }

      return (
        <TutorBookingScheduleBlock schedule={proposedSchedule} emphasize statusChip={statusChip} />
      );
    }

    return (
      <TutorBookingScheduleBlock
        schedule={formatBookingSchedule(booking.startTime, booking.endTime)}
        statusChip={statusChip}
      />
    );
  };

  const handleFilterChange = (filter: TutorBookingFilter) => {
    setActiveFilter(filter);
  };

  const handleCancelClick = (booking: UserTutorBookingListItem) => {
    setCancelSeriesId(null);
    setSelectedForCancel(booking);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelSeriesClick = (seriesId: string, booking: UserTutorBookingListItem) => {
    setCancelSeriesId(seriesId);
    setSelectedForCancel(booking);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleOpenDetails = (booking: UserTutorBookingListItem) => {
    navigate(`/user/tutor-bookings/${booking.id}`);
  };

  const handleCancelBooking = async () => {
    if (!selectedForCancel && !cancelSeriesId) return;
    setCancelling(true);
    try {
      if (cancelSeriesId) {
        await userApiClient.cancelTutorBookingSeries(cancelSeriesId, cancelReason);
      } else if (selectedForCancel) {
        await userApiClient.cancelTutorBooking(selectedForCancel.id, cancelReason);
      }
      await refreshAfterMutation();
      setCancelDialogOpen(false);
      setSelectedForCancel(null);
      setCancelSeriesId(null);
      setCancelReason('');
      setError(null);
    } catch {
      setError(t('userTutorBookings.cancelError', 'Failed to cancel booking'));
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirmAttendance = async (booking: UserTutorBookingListItem) => {
    setConfirmingBookingId(booking.id);
    try {
      await userApiClient.confirmTutorBookingAttendance(booking.id);
      await refreshAfterMutation();
      setError(null);
    } catch (err) {
      setError(getTutorLessonConfirmationErrorMessage(err, t));
    } finally {
      setConfirmingBookingId(null);
    }
  };

  const canCancel = (booking: UserTutorBookingListItem) => {
    if (
      activeFilter !== 'needs_user_action' &&
      activeFilter !== 'needs_tutor_action' &&
      activeFilter !== 'upcoming' &&
      activeFilter !== 'all'
    ) {
      return false;
    }
    if (isUserTutorBookingCancelledStatus(booking.status)) return false;
    const end = parseBookingEnd(booking.endTime);
    if (end.getTime() < Date.now()) return false;
    return true;
  };

  const showCancellationPolicy = (booking: UserTutorBookingListItem) => {
    if (activeFilter === 'past' || activeFilter === 'cancelled') return false;
    if (isUserTutorBookingCancelledStatus(booking.status)) return false;
    if (isPastTutorBookingStart(booking.startTime)) return false;
    if (!shouldShowCancellationPolicyForPayment(booking.paymentStatus)) return false;
    return true;
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyMessage = () => {
    const emptyKey = activeFilter === 'needs_tutor_action' ? 'awaiting_tutor' : activeFilter;
    return t(`tutorBookingFilters.empty.${emptyKey}`, {
      defaultValue: t('userTutorBookings.noConfirmed'),
    });
  };

  const renderEmpty = () => (
    <Card elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
      <Typography variant="h6" gutterBottom>
        {emptyMessage()}
      </Typography>
      {activeFilter !== 'past' && (
        <Button variant="contained" color="primary" onClick={() => navigate(defaultTutorSearchPath())} sx={{ mt: 2 }}>
          {t('userTutorBookings.browseTutors')}
        </Button>
      )}
    </Card>
  );

  const renderBookingCard = (booking: UserTutorBookingListItem) => {
    const pendingProposal = hasPendingChangeProposal(booking);
    const displayTitle = booking.tutorName?.trim() || t('userTutorBookings.tutorSession');
    const showCancel = canCancel(booking);
    const showConfirmAttendance = canUserConfirmTutorLessonAttendance({
      status: booking.status,
      userConfirmedAt: booking.userConfirmedAt,
      confirmAttendanceEnabled: booking.confirmAttendanceEnabled,
    });
    const showAttendanceChip = shouldShowTutorLessonAttendanceUi({
      status: booking.status,
      userConfirmedAt: booking.userConfirmedAt,
      confirmAttendanceEnabled: booking.confirmAttendanceEnabled,
    });
    const confirmingThis = confirmingBookingId === booking.id;

    return (
      <Card
        variant="outlined"
        sx={(theme) => ({
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: theme.palette.background.paper,
          borderColor: pendingProposal ? theme.palette.warning.main : theme.palette.divider,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: pendingProposal
              ? theme.palette.warning.light
              : alpha(theme.palette.primary.main, 0.5),
            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.08)}`,
          },
        })}
      >
        <CardContent sx={{ flex: 1, px: { xs: 2, sm: 2.5 }, pt: 2.5, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                src={(booking.tutorProfileImageUrl ?? booking.profileImageUrl)?.trim() || undefined}
                alt=""
                variant="rounded"
                sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  bgcolor: '#4AA7C6',
                  color: '#ffffff',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                }}
              >
                {tutorAvatarInitials(displayTitle)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    fontWeight={700}
                    noWrap
                    sx={{ lineHeight: 1.3, minWidth: 0 }}
                  >
                    {displayTitle}
                  </Typography>
                  {booking.tutorUserId?.trim() ? (
                    <ChatWithUserIconButton
                      partnerUserId={booking.tutorUserId}
                      partnerName={displayTitle}
                      basePath="/user/chats"
                      sx={{ flexShrink: 0 }}
                    />
                  ) : null}
                </Stack>
                {!booking.tutorName?.trim() ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {t('userTutorBookings.tutorId')}: {shortRef(booking.tutorId)}
                  </Typography>
                ) : null}
                <TutorSchoolAffiliation
                  spaceId={booking.spaceId}
                  spaceName={booking.spaceName}
                  organizationType={booking.organizationType}
                />
              </Box>
              <Chip
                color={getTutorBookingStatusChipColor(booking.status)}
                variant={getTutorBookingStatusChipVariant(booking.status)}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {statusLabel(booking.status)}
                    </Box>
                    {showAttendanceChip ? (
                      <TutorLessonAttendanceChip
                        status={booking.status}
                        userConfirmedAt={booking.userConfirmedAt}
                        confirmAttendanceEnabled={booking.confirmAttendanceEnabled}
                        confirming={confirmingThis}
                        onConfirm={
                          showConfirmAttendance ? () => handleConfirmAttendance(booking) : undefined
                        }
                        tone="inherit"
                      />
                    ) : null}
                  </Box>
                }
                sx={{
                  flexShrink: 0,
                  height: 48,
                  borderRadius: 1.5,
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  letterSpacing: '0.01em',
                  '& .MuiChip-label': {
                    px: 1.5,
                    whiteSpace: 'normal',
                    lineHeight: 1.25,
                    textAlign: 'center',
                  },
                }}
              />
            </Stack>

            <Stack spacing={0.75}>
              {renderCardScheduleSection(booking)}
            </Stack>

            <Box
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <MusicNoteRoundedIcon
                  sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                />
                <Stack spacing={0.75} minWidth={0} sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {booking.lessonOptionName?.trim() || t('userTutorBookings.tutorSession')}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TutorLessonSessionTypeChip sessionType={booking.lessonSessionType} />
                    <TutorBookingPaymentStatusChips
                      paymentStatus={booking.pass ? undefined : booking.paymentStatus}
                      stripeRefundStatus={booking.stripeRefundStatus ?? undefined}
                      compact
                    />
                    {booking.pass && <PassBookingChip pass={booking.pass} />}
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ flexShrink: 0, alignSelf: 'center' }}
                >
                  <PaymentsRoundedIcon
                    sx={{
                      fontSize: 18,
                      color: booking.pass ? 'text.secondary' : 'text.primary',
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={booking.pass ? 'text.secondary' : 'text.primary'}
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                      textDecoration: booking.pass ? 'line-through' : 'none',
                    }}
                  >
                    {formatMoney(booking.totalAmount, booking.currencyCode)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {showCancellationPolicy(booking) ? (
              <BookingCancellationPolicy
                startTime={booking.startTime}
                timezone={booking.timezone}
                policy={booking.cancellationPolicy}
                showWhenMissing={false}
              />
            ) : null}

            <BookingCreatedAtLabel
              createdAt={booking.createdAt}
              timezone={booking.timezone}
              placement="footer"
            />
          </Stack>
        </CardContent>

        <Divider />

        <CardActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': { m: 0 },
          }}
        >
          {pendingProposal ? (
            <Button
              variant="contained"
              color="warning"
              size="medium"
              onClick={() => handleOpenDetails(booking)}
              sx={(theme) => ({
                flex: { sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                // Darker gold (same hue as the schedule accent) so inherited white label stays readable.
                // Global `.MuiButton-root { color: inherit !important }` overrides MUI contrastText.
                bgcolor: theme.palette.warning.dark,
                fontWeight: 700,
                '&:hover': {
                  bgcolor: '#9A6E2A',
                },
              })}
            >
              {t('userTutorBookings.changeProposal.reviewChange', 'Review & respond')}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="medium"
              onClick={() => handleOpenDetails(booking)}
              sx={{ flex: { sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
            >
              {t('common.details')}
            </Button>
          )}
          {showCancel ? (
            <Button
              variant="outlined"
              color="error"
              size="medium"
              onClick={() => handleCancelClick(booking)}
              sx={{ flex: { sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
            >
              {t('common.cancel')}
            </Button>
          ) : null}
        </CardActions>
      </Card>
    );
  };

  const renderBookingCards = () => {
    const isEmptyTab = !loading && bookings.length === 0 && hasLoadedOnce;

    return (
      <Box
        sx={{
          pt: 3,
          opacity: loading && hasLoadedOnce && !isEmptyTab ? 0.65 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        {isEmptyTab ? (
          renderEmpty()
        ) : (
          <>
            <Grid container spacing={2}>
              {groupUserTutorBookings(bookings).map((entry) =>
                entry.kind === 'series' ? (
                  <Grid item xs={12} sm={6} key={entry.seriesId}>
                    <UserTutorSeriesBookingCard
                      bookings={entry.bookings}
                      onOpen={handleOpenDetails}
                      onCancel={() => handleCancelSeriesClick(entry.seriesId, entry.bookings[0])}
                      formatWhen={(startTime, endTime) => {
                        const labels = formatBookingSchedule(startTime, endTime);
                        return `${labels.dateLabel} · ${labels.timeLabel}`;
                      }}
                      formatAmount={formatMoney}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} key={entry.booking.id}>
                    {renderBookingCard(entry.booking)}
                  </Grid>
                ),
              )}
            </Grid>

            {totalElements > 0 ? (
              <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage={t('common.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                  t('common.pagination.displayedRows', { from, to, count })
                }
                sx={{ mt: 2 }}
              />
            ) : null}
          </>
        )}
      </Box>
    );
  };

  const showInitialLoading = loading && !hasLoadedOnce;

  return (
    <>
      <SEO title={t('userTutorBookings.title')} noindex />
      <Box
        sx={{
          py: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 2 },
          pb: { xs: 8, sm: 10 },
          maxWidth: USER_TUTOR_BOOKINGS_MAX_WIDTH,
          width: '100%',
          mx: 'auto',
        }}
      >
        <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {t('userTutorBookings.title')}
          </Typography>
          <Divider sx={{ mb: { xs: 2, sm: 4 } }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {showInitialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TutorBookingFilterTabs
                filters={USER_TUTOR_BOOKING_FILTERS}
                activeFilter={activeFilter}
                summary={summary}
                onChange={handleFilterChange}
                labelOverrides={{
                  needs_tutor_action: t('tutorBookingFilters.awaiting_tutor', 'Waiting for the tutor'),
                }}
              />
              {renderBookingCards()}
            </>
          )}
        </Card>
      </Box>

      <Dialog open={cancelDialogOpen} onClose={() => !cancelling && setCancelDialogOpen(false)}>
        <DialogTitle>
          {cancelSeriesId
            ? t('userTutorBookings.cancelAllTitle', 'Cancel all lessons?')
            : t('bookings.cancelBooking')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {cancelSeriesId
              ? t(
                  'userTutorBookings.cancelAllBody',
                  'These lessons are still waiting for approval. Cancelling releases the card hold for the whole series.',
                )
              : t('userTutorBookings.cancelConfirmation')}
          </Typography>
          {selectedForCancel &&
          shouldShowCancellationPolicyForPayment(selectedForCancel.paymentStatus) ? (
            <BookingCancellationPolicy
              startTime={selectedForCancel.startTime}
              timezone={selectedForCancel.timezone}
              policy={selectedForCancel.cancellationPolicy}
              showWhenMissing={false}
              density="comfortable"
              sx={{ mb: 2 }}
            />
          ) : null}
          <TextField
            fullWidth
            margin="dense"
            label={t('userTutorBookings.cancelReasonOptional')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            {t('bookings.keepBooking')}
          </Button>
          <Button onClick={handleCancelBooking} color="error" variant="contained" disabled={cancelling}>
            {cancelling
              ? t('bookings.cancelling')
              : cancelSeriesId
                ? t('userTutorBookings.cancelAll', 'Cancel all')
                : t('bookings.confirmCancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
